import React, { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import { Info, PlusCircle } from "feather-icons-react/build/IconComponents";
import { Controller, useForm } from "react-hook-form";
import InputField from "../../components/Input";
import BrandServices from "../../../services/Brand";
import CategoryServices from "../../../services/Category";
import ProductServices from "../../../services/Product";
import { ErrorToaster, SuccessToaster } from "../../components/toaster/Toaster";
import SelectField from "../../components/Select";
import { Form, Input, Popconfirm, Select, Table } from 'antd';
import { v4 as uuidv4 } from 'uuid';

const EditableCell = ({
  editable,
  required,
  dataIndex,
  title,
  record,
  index,
  children,
  handleSave,
  ...restProps
}) => {
  const save = async (e) => {
    try {
      handleSave(index, { [dataIndex]: e.target.value });
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  return (
    <td {...restProps}>
      {editable ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: required,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          <Input defaultValue={record[dataIndex]} onPressEnter={(e) => save(e)} onBlur={(e) => save(e)} />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const ProductForm = ({ onSubmit, loading }) => {

  const route = all_routes;
  const { id } = useParams();

  const { register, handleSubmit, formState: { errors }, setValue, control } = useForm();

  // *For Product Images
  const [productImages, setProductImages] = useState([]);

  // *For Brands
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // *For Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // *For Variant Options
  const [variantOptions, setVariantOptions] = useState([{ id: uuidv4(), keyOption: [], valueOption: [] }]);

  // *For Variants
  const [variants, setVariants] = useState([]);

  // *For Variant Loading
  const [variantLoading, setVariantLoading] = useState(false);

  // *For Get Brands
  const getBrands = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await BrandServices.getBrands(params)
      setBrands(data)
    } catch (error) {
      console.log('getBrands => error:', error)
    }
  }

  // *For Get Categories
  const getCategories = async () => {
    try {
      let params = {
        page: 1,
        limit: 50,
        active: true
      }
      const { data } = await CategoryServices.getCategories(params)
      setCategories(data)
    } catch (error) {
      console.log('getCategories => error:', error)
    }
  }

  // *For Add Variation
  const addVariant = async () => {
    try {
      const updateVariantOptions = [...variantOptions]
      let obj = { id: uuidv4() }
      updateVariantOptions.push(obj)
      setVariantOptions(updateVariantOptions)
    } catch (error) {
      console.log('addVariation => error:', error)
    }
  }

  // *For Delete Variant
  const deleteVariant = (id) => {
    try {
      const index = variantOptions.findIndex((e) => e.id === id)
      let updateVariantOptions = [...variantOptions]
      updateVariantOptions.splice(index, 1);
      setVariantOptions(updateVariantOptions)
    } catch (error) {
      console.log('deleteVariant => error:', error)
    }
  }

  // *For Upload Image
  const handleUploadImage = (e) => {
    try {
      const images = [...productImages]
      if (e.target.value !== "") {
        const file = e.target.files[0]
        const imgReader = new FileReader();
        imgReader.onload = (event) => {
          let obj = {
            file: file,
            image: event.target.result
          }
          images.push(obj)
          setProductImages(images)
        };
        imgReader.readAsDataURL(file);
      }
    } catch (error) {
      console.log('handleUploadImage => error:', error)
    }
  };

  // *For Remove Image 
  const handleRemoveImage = async (index) => {
    try {
      const images = [...productImages]
      images.splice(index, 1);
      setProductImages(images)
    } catch (error) {
      console.log('handleRemoveImage => error:', error)
    }
  }

  // *For Submit Form
  const submitForm = async (formData) => {
    try {
      const variantsOptions = {};

      Object.entries(formData).forEach(([key, value]) => {
        if (key.startsWith("key_")) {
          const id = key.replace("key_", "");
          const variantKey = String(value).replace(/\s+/g, '_');
          const variantValue = formData[`value_${id}`];

          if (Array.isArray(variantValue) && variantKey) {
            variantsOptions[variantKey] = variantValue;
          }
        }
      });
      let obj = {
        name: formData?.name,
        price: parseFloat(formData?.price),
        remarks: formData?.remarks,
        is_active: formData?.status,
        code: formData?.sku,
        spiff: parseFloat(formData?.spiff),
        brand_id: selectedBrand?.id,
        category_id: selectedCategory?.id,
        variantsOptions: variantsOptions
      }
      console.log('obj', obj)
      onSubmit(obj)
    } catch (error) {
      ErrorToaster(error)
    }
  }

  // *For Get Product Detail
  const getProductDetail = async () => {
    try {
      const { data } = await ProductServices.getProductById(id)
      setValue('name', data[0]?.name)
      setValue('price', data[0]?.price)
      setValue('remarks', data[0]?.remarks)
      setValue('status', data[0]?.is_active)
      setValue('sku', data[0]?.code)
      setValue('spiff', data[0]?.spiff)
      setValue('brand', data[0]?.brand?.name)
      setValue('category', data[0]?.category?.name)
      setSelectedBrand(data[0]?.brand)
      setSelectedCategory(data[0]?.category)
      setVariants(data[0]?.variants)

      const options = [];
      const keyValues = [];
      Object.entries(data[0]?.variant_options).forEach(([key, value]) => {
        const optionId = uuidv4();
        options.push({ id: optionId });
        keyValues.push({ optionId, key, value });
      })
      setVariantOptions(options)

      setTimeout(() => {
        keyValues.forEach(({ optionId, key, value }) => {
          setValue(`key_${optionId}`, key);
          setValue(`value_${optionId}`, value);
        });
      }, 10);
    } catch (error) {
      ErrorToaster(error)
    }
  }

  // *For Variant Edit Save
  const handleSave = (index, value) => {
    try {
      const newData = [...variants];
      const item = newData[index];
      newData.splice(index, 1, { ...item, ...value });
      setVariants(newData);
    } catch (error) {
      console.log('handleSave => error:', error)
    }
  };

  // *For Save Variants
  const saveVariants = async () => {
    setVariantLoading(true)
    try {
      const variantsArray = []
      variants.forEach((variant) => {
        variantsArray.push({
          variant_id: variant?.id,
          sku: variant?.sku,
          reg_price: Number(variant?.reg_price)
        })
      })
      await ProductServices.updateVariants(id, variantsArray)
      SuccessToaster('Variants Update Successfully')
      navigate(route?.products)
    } catch (error) {
      ErrorToaster(error)
    } finally {
      setVariantLoading(false)
    }
  }

  // *For Delete Variant
  const handleDeleteVariant = async (id) => {
    try {
      await ProductServices.deleteVariant(id)
      SuccessToaster('Variant Delete Successfully')

      const index = variants.findIndex((e) => e.id === id)
      const updateVariants = [...variants]
      updateVariants.splice(index, 1);
      setVariants(updateVariants)
    } catch (error) {
      ErrorToaster(error)
    }
  }

  const defaultColumns = [
    {
      title: "Variant",
      dataIndex: "attributes",
      render: (text, record) => (
        <span>
          {Object.values(record?.attributes).join('-')}
        </span>
      )
    },
    {
      title: "SKU",
      dataIndex: "sku",
      width: '30%',
      editable: true,
      render: (text) => (
        <span>
          {text}
        </span>
      ),
    },
    {
      title: "Reg Price",
      dataIndex: "reg_price",
      width: '30%',
      editable: true,
      render: (text) => (
        <span>
          {text}
        </span>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (_, record) => (
        <Popconfirm title="Sure to delete?" onConfirm={() => handleDeleteVariant(record.id)}>
          <a className="text-danger">Delete</a>
        </Popconfirm>
      )
    },
  ];

  const columns = defaultColumns.map(col => {
    if (!col.editable) {
      return col;
    }
    return Object.assign(Object.assign({}, col), {
      onCell: (record, index) => ({
        index,
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    });
  });

  useEffect(() => {
    getBrands()
    getCategories()
  }, []);

  useEffect(() => {
    if (id) {
      getProductDetail()
    }
  }, [id]);

  // useEffect(() => {
  //   const a = {
  //     "Size": [
  //       "Queen",
  //       "King",
  //       "Twin"
  //     ],
  //     "Frimness_Level": [
  //       "Soft",
  //       "Medium"
  //     ]
  //   }
  //   if (selectedCategory?.id) {
  //     const staticKeys = []
  //     Object.entries(selectedCategory?.static_options).forEach(([key, value]) => {
  //       staticKeys.push({
  //         keyOption: key,
  //         valueOption: value
  //       })
  //     })
  //     console.log(' useEffect => staticKeys:', staticKeys)


  //     const staticOption = []
  //     variantOptions.forEach((e) => {
  //       staticOption.push({
  //         id: e.id,
  //         keyOption: '',
  //         value: ''
  //       })
  //     })
  //     setVariantOptions()
  //   }
  // }, [selectedCategory]);

  return (
    <div className="card">
      <div className="card-body add-product pb-0">
        <div className="mb-3">
          <div className="addproduct-icon">
            <h5>
              <Info className="add-info" />
              <span>Information</span>
            </h5>
          </div>
          <form onSubmit={handleSubmit(submitForm)}>
            <div className="row align-items-center">
              {/* <div className="col-lg-12 col-sm-12 col-12">
                <div className="add-choosen">
                  <div className="input-blocks">
                    <div className="image-upload" style={{ height: '123px', width: '137px' }}>
                      <input type="file" onChange={(e) => handleUploadImage(e)} />
                      <div className="image-uploads" style={{ padding: '43px 0px' }}>
                        <PlusCircle className="plus-down-add me-0" />
                        <h4>Add Image</h4>
                      </div>
                    </div>
                  </div>
                  {productImages.length > 0 &&
                    productImages.map((item, index) => (
                      <div key={index} className="phone-img">
                        <img
                          src={item?.image}
                          alt="image"
                        />
                        <Link to="#">
                          <X
                            className="x-square-add remove-product"
                            onClick={() => handleRemoveImage(index)}
                          />
                        </Link>
                      </div>
                    ))
                  }
                </div>
              </div> */}
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="mb-3 add-product">
                  <InputField
                    label="Name"
                    type="text"
                    error={errors?.name?.message}
                    register={register("name", {
                      required: 'Please enter product name.',
                    })}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="mb-3 add-product">
                  <SelectField
                    label={"Brand"}
                    options={brands}
                    selected={selectedBrand}
                    onSelect={(value) => setSelectedBrand(value)}
                    error={errors?.brand?.message}
                    register={register("brand", {
                      required: 'Please select brand.'
                    })}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="mb-3 add-product">
                  <SelectField
                    label={"Category"}
                    options={categories}
                    selected={selectedCategory}
                    onSelect={(value) => setSelectedCategory(value)}
                    error={errors?.category?.message}
                    register={register("category", {
                      required: 'Please select category.'
                    })}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="mb-3 add-product">
                  <InputField
                    label="Reg Price"
                    type="number"
                    error={errors?.price?.message}
                    register={register("price", {
                      required: 'Please enter product price.',
                    })}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="mb-3 add-product">
                  <InputField
                    label="SKU"
                    error={errors?.sku?.message}
                    register={register("sku", {
                      required: 'Please enter product sku.',
                    })}
                  />
                </div>
              </div>
              <div className="col-lg-3 col-sm-3 col-6">
                <div className="mb-3 add-product">
                  <InputField
                    label="Spiff"
                    type="text"
                    register={register("spiff")}
                  />
                </div>
              </div>
              <div className="col-lg-6 col-sm-6 col-12">
                <div className="mb-3 add-product">
                  <InputField
                    label="Remarks"
                    inputType="textarea"
                    placeholder="Type Remarks"
                    register={register("remarks")}
                  />
                </div>
              </div>
              <div className="col-lg-6 col-sm-6 col-6">
                <div className="status-toggle modal-status d-flex justify-content-between align-items-center">
                  <span className="status-label">Status</span>
                  <InputField
                    id="checkbox1"
                    type="checkbox"
                    className="check"
                    defaultChecked={true}
                    register={register("status")}
                  />
                </div>
              </div>
              <div className="col-lg-12 col-sm-12 col-12 mt-3 mb-2">
                <h4>Variant Options</h4>
              </div>
              {variantOptions?.length > 0 &&
                variantOptions.map((item, index) => (
                  <Fragment key={index}>
                    <div className="col-lg-3 col-sm-3 col-3 mt-2">
                      <div className="mb-3">
                        <label className="form-label">{`Option ${index + 1}`}</label>
                        <Controller
                          control={control}
                          name={`key_${item?.id}`}
                          render={({ field }) => (
                            <Select
                              {...field}
                              mode="tags"
                              maxCount={1}
                              size="large"
                              style={{ width: '100%' }}
                              options={item?.keyOption || []}
                              onChange={(value) => field.onChange(value)}
                              value={field.value || []}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className="col-lg-8 col-sm-8 col-3 mt-2">
                      <div className="mb-3">
                        <label className="form-label">{`Value ${index + 1}`}</label>
                        <Controller
                          control={control}
                          name={`value_${item?.id}`}
                          render={({ field }) => (
                            <Select
                              {...field}
                              mode="tags"
                              size="large"
                              style={{ width: '100%' }}
                              options={item?.valueOption || []}
                              onChange={(value) => field.onChange(value)}
                              value={field.value || []}
                            />
                          )}
                        />
                      </div>
                    </div>
                    {variantOptions.length > 1 &&
                      <div className="col-lg-1 col-sm-1 col-3 mt-2">
                        <p className="text-danger text-center" style={{ cursor: 'pointer' }} onClick={() => deleteVariant(item?.id)}>Remove</p>
                      </div>
                    }
                  </Fragment>
                ))
              }
              <div className="col-lg-3 col-sm-3 col-3 mt-2">
                <button
                  type="button"
                  className="btn btn-submit"
                  onClick={() => addVariant()}
                >
                  <PlusCircle className="plus-down-add" />
                  Add Option
                </button>
              </div>
              <div className="col-lg-12">
                <div className="btn-addproduct mb-4">
                  <Link to={route.products} className="btn btn-cancel me-2">
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-submit"
                    disabled={loading}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </form>
          {variants?.length > 0 &&
            <Fragment>
              <div className="addproduct-icon">
                <h5>
                  <Info className="add-info" />
                  <span>Variants</span>
                </h5>
              </div>
              <div className="table-responsive">
                <Table
                  components={{
                    body: {
                      cell: EditableCell,
                    },
                  }}
                  rowClassName={() => 'editable-row'}
                  bordered
                  dataSource={variants}
                  columns={columns}
                  pagination={false}
                />
              </div>
              <div className="btn-addproduct mb-4 mt-4">
                <button
                  type="button"
                  className="btn btn-submit"
                  disabled={variantLoading}
                  onClick={() => saveVariants()}
                >
                  Save Variant
                </button>
              </div>
            </Fragment>
          }
        </div>
      </div>
    </div>
  );
};

export default ProductForm;
